import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { COUNTRY_CURRENCY, CODE_TO_NAME, ALL_COUNTRIES } from '../data/countries';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aliases for backward compatibility within this file
const COUNTRY_TO_CURRENCY = COUNTRY_CURRENCY;
const COUNTRY_NAMES = CODE_TO_NAME;

const CurrencyContext = createContext(null);

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

// Debug logging helper
const logDetection = (stage, data) => {
    console.log(`[CurrencyDetection] ${stage}:`, data);
};

export const CurrencyProvider = ({ children }) => {
    const [currencies, setCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [baseCurrency, setBaseCurrency] = useState('USD');
    const [exchangeRates, setExchangeRates] = useState({});
    const [loading, setLoading] = useState(true);
    const [detectedCountry, setDetectedCountry] = useState(null);
    const [detectedCountryName, setDetectedCountryName] = useState(null);
    const [detectionSource, setDetectionSource] = useState(null);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showCountrySelector, setShowCountrySelector] = useState(false);

    // Fetch currencies on mount
    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                const response = await fetch(`${API_URL}/currencies`);
                if (response.ok) {
                    const data = await response.json();
                    setCurrencies(data.currencies || []);
                    setBaseCurrency(data.base_currency || 'USD');
                    
                    const rates = {};
                    data.currencies?.forEach(c => {
                        rates[c.code] = c.rate;
                    });
                    setExchangeRates(rates);
                }
            } catch (error) {
                console.error('Failed to fetch currencies:', error);
            }
        };
        fetchCurrencies();
    }, []);

    // Initialize currency detection on mount
    useEffect(() => {
        const initializeCurrency = async () => {
            // Check for manual override first
            const manualOverride = localStorage.getItem('manual_currency_override');
            const savedCurrency = localStorage.getItem('selected_currency');
            const savedCountry = localStorage.getItem('manual_country_override');
            
            if (manualOverride === 'true' && savedCurrency) {
                logDetection('Manual Override', { currency: savedCurrency, country: savedCountry });
                setSelectedCurrency(savedCurrency);
                if (savedCountry) {
                    setDetectedCountry(savedCountry);
                    setDetectedCountryName(COUNTRY_NAMES[savedCountry] || savedCountry);
                }
                setDetectionSource('manual');
                setLoading(false);
                return;
            }

            // Check if we've already asked for location permission
            const permissionAsked = localStorage.getItem('location_permission_asked');
            
            if (!permissionAsked) {
                // First visit - show location permission modal after a short delay
                setTimeout(() => {
                    setShowLocationModal(true);
                }, 1500);
                setLoading(false);
            } else if (permissionAsked === 'granted') {
                // Permission was granted - try geolocation
                await detectFromGeolocation();
            } else {
                // Permission denied or skipped - use fallback
                await detectFromFallback();
            }
        };

        initializeCurrency();
    }, []);

    // Reverse geocode coordinates to country code using multiple services
    const reverseGeocode = async (latitude, longitude) => {
        logDetection('Reverse Geocoding', { latitude, longitude });
        
        // Try Nominatim first
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=3&addressdetails=1`,
                { 
                    headers: { 'User-Agent': 'BiscuitBrand/1.0' },
                    timeout: 10000
                }
            );
            if (response.ok) {
                const data = await response.json();
                logDetection('Nominatim Response', data);
                
                const countryCode = data.address?.country_code?.toUpperCase();
                const countryName = data.address?.country;
                const city = data.address?.city || data.address?.town || data.address?.village;
                const region = data.address?.state || data.address?.county;
                
                if (countryCode) {
                    logDetection('Nominatim Success', { countryCode, countryName, city, region });
                    return { 
                        countryCode, 
                        countryName: countryName || COUNTRY_NAMES[countryCode] || countryCode,
                        city,
                        region 
                    };
                }
            }
        } catch (error) {
            logDetection('Nominatim Error', error.message);
        }

        // Fallback to BigDataCloud (free, no API key required)
        try {
            const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            if (response.ok) {
                const data = await response.json();
                logDetection('BigDataCloud Response', data);
                
                const countryCode = data.countryCode?.toUpperCase();
                const countryName = data.countryName;
                const city = data.city || data.locality;
                const region = data.principalSubdivision;
                
                if (countryCode) {
                    logDetection('BigDataCloud Success', { countryCode, countryName, city, region });
                    return { 
                        countryCode, 
                        countryName: countryName || COUNTRY_NAMES[countryCode] || countryCode,
                        city,
                        region 
                    };
                }
            }
        } catch (error) {
            logDetection('BigDataCloud Error', error.message);
        }

        logDetection('Reverse Geocoding Failed', 'All services failed');
        return null;
    };

    // Detect location using browser geolocation
    const detectFromGeolocation = async () => {
        logDetection('Starting Geolocation Detection', 'navigator.geolocation');
        
        if (!navigator.geolocation) {
            logDetection('Geolocation Not Supported', 'Falling back');
            return detectFromFallback();
        }

        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    resolve, 
                    reject, 
                    {
                        enableHighAccuracy: true, // Request high accuracy
                        timeout: 15000, // 15 second timeout
                        maximumAge: 300000 // 5 minute cache
                    }
                );
            });

            const { latitude, longitude, accuracy } = position.coords;
            logDetection('Geolocation Coordinates', { latitude, longitude, accuracy: `${accuracy}m` });

            // Reverse geocode the coordinates
            const geoResult = await reverseGeocode(latitude, longitude);
            
            if (geoResult && geoResult.countryCode) {
                const { countryCode, countryName, city, region } = geoResult;
                const currency = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
                
                logDetection('Final Detection Result', {
                    countryCode,
                    countryName,
                    city,
                    region,
                    currency,
                    source: 'geolocation'
                });

                setDetectedCountry(countryCode);
                setDetectedCountryName(countryName);
                setSelectedCurrency(currency);
                setDetectionSource('geolocation');
                
                localStorage.setItem('selected_currency', currency);
                localStorage.setItem('detected_country', countryCode);
                localStorage.setItem('detected_country_name', countryName);
                
                setLoading(false);
                return currency;
            } else {
                logDetection('Reverse Geocoding Failed', 'Falling back to other methods');
            }
        } catch (error) {
            logDetection('Geolocation Error', { 
                code: error.code, 
                message: error.message,
                PERMISSION_DENIED: error.code === 1,
                POSITION_UNAVAILABLE: error.code === 2,
                TIMEOUT: error.code === 3
            });
        }

        // If geolocation fails, try fallback methods
        return detectFromFallback();
    };

    // Fallback detection using timezone, locale, and IP
    const detectFromFallback = async () => {
        setLoading(true);
        logDetection('Starting Fallback Detection', 'Multiple methods');

        // Method 1: Try to detect from timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        logDetection('Timezone', timezone);
        
        const timezoneToCountry = {
            'Europe/London': 'GB',
            'Europe/Dublin': 'IE',
            'Europe/Paris': 'FR',
            'Europe/Berlin': 'DE',
            'Europe/Rome': 'IT',
            'Europe/Madrid': 'ES',
            'Europe/Amsterdam': 'NL',
            'Europe/Brussels': 'BE',
            'Europe/Vienna': 'AT',
            'Europe/Lisbon': 'PT',
            'Europe/Helsinki': 'FI',
            'Europe/Athens': 'GR',
            'America/New_York': 'US',
            'America/Chicago': 'US',
            'America/Denver': 'US',
            'America/Los_Angeles': 'US',
            'America/Toronto': 'CA',
            'America/Vancouver': 'CA',
            'Africa/Lagos': 'NG',
            'Australia/Sydney': 'AU',
            'Australia/Melbourne': 'AU',
            'Pacific/Auckland': 'NZ',
            'Asia/Tokyo': 'JP',
            'Asia/Seoul': 'KR',
            'Asia/Kolkata': 'IN',
        };

        const tzCountry = timezoneToCountry[timezone];
        if (tzCountry) {
            logDetection('Timezone Match', { timezone, country: tzCountry });
        }

        // Method 2: Try browser locale
        const browserLang = navigator.language || navigator.userLanguage;
        logDetection('Browser Language', browserLang);
        
        const langToCountry = {
            'en-GB': 'GB', 'en-UK': 'GB',
            'en-US': 'US',
            'en-CA': 'CA', 'fr-CA': 'CA',
            'en-AU': 'AU',
            'en-NZ': 'NZ',
            'en-IE': 'IE',
            'de-DE': 'DE', 'de-AT': 'AT', 'de-CH': 'DE',
            'fr-FR': 'FR', 'fr-BE': 'BE',
            'it-IT': 'IT',
            'es-ES': 'ES', 'es-MX': 'MX',
            'nl-NL': 'NL', 'nl-BE': 'BE',
            'pt-PT': 'PT', 'pt-BR': 'BR',
        };

        let langCountry = null;
        for (const [lang, country] of Object.entries(langToCountry)) {
            if (browserLang?.toLowerCase().startsWith(lang.toLowerCase())) {
                langCountry = country;
                logDetection('Language Match', { lang, country });
                break;
            }
        }

        // Method 3: Backend IP detection
        let ipCountry = null;
        try {
            const response = await fetch(`${API_URL}/currencies/detect`);
            if (response.ok) {
                const data = await response.json();
                ipCountry = data.detected_country;
                logDetection('IP Detection', data);
            }
        } catch (error) {
            logDetection('IP Detection Error', error.message);
        }

        // Determine best country - prioritize non-US results if available
        let finalCountry = null;
        let detectionMethod = 'fallback';

        // Priority: Timezone > Language > IP
        if (tzCountry && tzCountry !== 'US') {
            finalCountry = tzCountry;
            detectionMethod = 'timezone';
        } else if (langCountry && langCountry !== 'US') {
            finalCountry = langCountry;
            detectionMethod = 'language';
        } else if (ipCountry && ipCountry !== 'US') {
            finalCountry = ipCountry;
            detectionMethod = 'ip';
        } else {
            // Use whatever we found, even if it's US
            finalCountry = tzCountry || langCountry || ipCountry || 'US';
            detectionMethod = tzCountry ? 'timezone' : (langCountry ? 'language' : (ipCountry ? 'ip' : 'default'));
        }

        const currency = COUNTRY_TO_CURRENCY[finalCountry] || 'USD';
        const countryName = COUNTRY_NAMES[finalCountry] || finalCountry;

        logDetection('Final Fallback Result', {
            country: finalCountry,
            countryName,
            currency,
            source: detectionMethod,
            allDetected: { timezone: tzCountry, language: langCountry, ip: ipCountry }
        });

        setDetectedCountry(finalCountry);
        setDetectedCountryName(countryName);
        setSelectedCurrency(currency);
        setDetectionSource(detectionMethod);
        
        localStorage.setItem('selected_currency', currency);
        localStorage.setItem('detected_country', finalCountry);
        localStorage.setItem('detected_country_name', countryName);
        
        setLoading(false);
        return currency;
    };

    // Handle user allowing location
    const handleAllowLocation = async () => {
        setShowLocationModal(false);
        localStorage.setItem('location_permission_asked', 'granted');
        logDetection('User Allowed Location', 'Starting geolocation');
        await detectFromGeolocation();
    };

    // Handle user denying location
    const handleDenyLocation = async () => {
        setShowLocationModal(false);
        localStorage.setItem('location_permission_asked', 'denied');
        logDetection('User Denied Location', 'Using fallback');
        await detectFromFallback();
    };

    // Manual currency change (marks as override)
    const changeCurrency = useCallback((code) => {
        logDetection('Manual Currency Change', { from: selectedCurrency, to: code });
        setSelectedCurrency(code);
        localStorage.setItem('selected_currency', code);
        localStorage.setItem('manual_currency_override', 'true');
    }, [selectedCurrency]);

    // Manual country change
    const changeCountry = useCallback((countryCode) => {
        const currency = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
        const countryName = COUNTRY_NAMES[countryCode] || countryCode;
        
        logDetection('Manual Country Change', { countryCode, countryName, currency });
        
        setDetectedCountry(countryCode);
        setDetectedCountryName(countryName);
        setSelectedCurrency(currency);
        setDetectionSource('manual');
        
        localStorage.setItem('selected_currency', currency);
        localStorage.setItem('detected_country', countryCode);
        localStorage.setItem('detected_country_name', countryName);
        localStorage.setItem('manual_country_override', countryCode);
        localStorage.setItem('manual_currency_override', 'true');
        
        setShowCountrySelector(false);
    }, []);

    // Convert price from base currency to selected currency
    const convertPrice = useCallback((price, fromCurrency = baseCurrency) => {
        if (!price || !exchangeRates[selectedCurrency]) return price;
        
        const fromRate = exchangeRates[fromCurrency] || 1;
        const toRate = exchangeRates[selectedCurrency] || 1;
        
        const basePrice = price / fromRate;
        return basePrice * toRate;
    }, [selectedCurrency, exchangeRates, baseCurrency]);

    // Format price with currency symbol
    const formatPrice = useCallback((price, currency = selectedCurrency) => {
        const currencyInfo = currencies.find(c => c.code === currency);
        const symbol = currencyInfo?.symbol || '$';
        const convertedPrice = currency === baseCurrency ? price : convertPrice(price);
        
        if (currency === 'NGN') {
            return `${symbol}${Math.round(convertedPrice).toLocaleString()}`;
        }
        
        return `${symbol}${convertedPrice.toFixed(2)}`;
    }, [selectedCurrency, currencies, baseCurrency, convertPrice]);

    // Get currency symbol
    const getCurrencySymbol = useCallback((code = selectedCurrency) => {
        const currency = currencies.find(c => c.code === code);
        return currency?.symbol || '$';
    }, [selectedCurrency, currencies]);

    // Reset all preferences
    const resetPreferences = useCallback(() => {
        localStorage.removeItem('selected_currency');
        localStorage.removeItem('manual_currency_override');
        localStorage.removeItem('manual_country_override');
        localStorage.removeItem('location_permission_asked');
        localStorage.removeItem('detected_country');
        localStorage.removeItem('detected_country_name');
        setShowLocationModal(true);
        logDetection('Preferences Reset', 'All cleared');
    }, []);

    // Full country list from shared data source
    const supportedCountries = ALL_COUNTRIES;

    const value = {
        currencies,
        selectedCurrency,
        baseCurrency,
        exchangeRates,
        loading,
        detectedCountry,
        detectedCountryName,
        detectionSource,
        showLocationModal,
        showCountrySelector,
        setShowCountrySelector,
        changeCurrency,
        changeCountry,
        convertPrice,
        formatPrice,
        getCurrencySymbol,
        handleAllowLocation,
        handleDenyLocation,
        resetPreferences,
        setShowLocationModal,
        supportedCountries,
        COUNTRY_TO_CURRENCY,
        COUNTRY_NAMES,
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

export default CurrencyContext;
